import { Range, defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DisplayMode } from "~/components/build/Show/lib/useFilterStore";
import EmptyState from "~/components/build/Show/routes/list/-components/EmptyState";
import { TreeItem } from "~/components/build/Show/routes/list/-components/TreeItem";
import { useScrollToId } from "~/components/build/Show/routes/list/-components/useScrollToId";
import {
  computeActiveStack,
  maxStickyHeight,
  stickyAncestorHeight,
  useStickyHeaders,
} from "~/components/build/Show/routes/list/-components/useStickyHeaders";
import { FlatTreeNode } from "~/components/build/Show/routes/list/-components/useTreeNodes";

interface StepTreeProps {
  /**
   * Ref for scrollable element with overflow-scroll
   */
  scrollRef: React.RefObject<HTMLDivElement>;

  /**
   * Item offset when rendering sticky items.
   */
  offsetTop?: number;

  /**
   * Tree display mode
   */
  displayMode: DisplayMode;

  /**
   * Flat list of nodes to render.
   */
  nodes: FlatTreeNode[];
}

/**
 * Render a virtualised tree of states, steps, and jobs with sticky headers
 */
export function StepTree({ nodes, displayMode, scrollRef, offsetTop = 0 }: StepTreeProps) {
  const activeStickyIndexRef = useRef<number[]>([]);

  // Populated by TreeItem itself when its `stickyIndex` is set, via a
  // mount-keyed useEffect — so the map only mutates on actual sticky
  // transitions, not on every render. useStickyHeaders reads this map
  // every scroll tick instead of re-querying the DOM.
  const stickyElsRef = useRef<Map<number, HTMLElement>>(new Map());

  // Indices of sticky-eligible rows. Drives the active-stack scan in
  // both rangeExtractor and useStickyHeaders without re-filtering each
  // call.
  const stickyNodeIndices = useMemo(() => {
    const indices: number[] = [];
    for (let idx = 0; idx < nodes.length; idx++) {
      if (nodes[idx].sticky) {
        indices.push(idx);
      }
    }
    return indices;
  }, [nodes]);

  // Cumulative starts per index, derived from node.height. Used as the
  // source of truth for sticky-threshold checks rather than the
  // virtualizer's measurementsCache — the cache can lag a frame after
  // tree changes or after a row re-enters the rendered window, which
  // used to produce visible sticky-flicks.
  const startByIndex = useMemo(() => {
    const starts = new Array<number>(nodes.length);
    let acc = 0;
    for (let idx = 0; idx < nodes.length; idx++) {
      starts[idx] = acc;
      acc += nodes[idx].height;
    }
    return starts;
  }, [nodes]);

  // stickyAncestorHeight() walks parentPath.reduce on every call. The
  // value is stable per node, and we read it in several per-scroll hot
  // paths (computeActiveStack, applyStickyPushTops) plus per-render in
  // the row style. Precompute once per nodes change so those reads are
  // O(1) array lookups instead of O(depth) reductions.
  const stickyAncestorHeightByIndex = useMemo(
    () => nodes.map((node) => stickyAncestorHeight(node)),
    [nodes],
  );

  const stickyPadding = useMemo(() => maxStickyHeight(nodes), [nodes]);

  const rangeExtractor = useCallback(
    (range: Range) => {
      activeStickyIndexRef.current = computeActiveStack(
        rowVirtualizer.scrollOffset ?? 0,
        stickyNodeIndices,
        nodes,
        startByIndex,
        stickyAncestorHeightByIndex,
        stickyPadding,
      );

      const defaultRange = defaultRangeExtractor(range);
      const allIndices = new Set([...defaultRange, ...activeStickyIndexRef.current]);
      return Array.from(allIndices).sort((left, right) => left - right);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeStickyIndexRef is a ref, rowVirtualizer is stable
    [nodes, stickyNodeIndices, startByIndex, stickyAncestorHeightByIndex, stickyPadding],
  );

  const rowVirtualizer = useVirtualizer({
    count: nodes.length,
    getItemKey: (index) => nodes[index].id,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => nodes[index].height,
    overscan: 50,
    rangeExtractor,
    scrollPaddingStart: stickyPadding,
    scrollPaddingEnd: stickyPadding,
  });

  // Force the virtualizer to re-detect the scroll element after mount.
  const [, setMounted] = useState(false);
  useLayoutEffect(() => {
    setMounted(true);
  }, []);

  useScrollToId(rowVirtualizer, nodes, activeStickyIndexRef);

  const lastStuckIndex = useStickyHeaders({
    scrollRef,
    rowVirtualizer,
    nodes,
    activeStickyIndexRef,
    stickyElsRef,
    stickyNodeIndices,
    startByIndex,
    stickyAncestorHeightByIndex,
    maxStickyHeight: stickyPadding,
    offsetTop,
  });

  if (nodes.length === 0) {
    return <EmptyState />;
  }

  const virtualItems = rowVirtualizer.getVirtualItems();
  // O(1) `isActiveSticky` per row vs. O(stackSize) `.includes()` — bounded
  // win for small stacks but cleaner.
  const activeStickySet = new Set(activeStickyIndexRef.current);

  return (
    <div className="p-1 md:p-0">
      <div role="tree" style={{ height: rowVirtualizer.getTotalSize() }} className="relative">
        {virtualItems.map((virtualItem) => {
          const node = nodes[virtualItem.index];
          const isSticky = activeStickySet.has(virtualItem.index);
          const isLastSticky = virtualItem.index === lastStuckIndex;
          const adjustedDepth = displayMode === DisplayMode.State ? node.depth - 1 : node.depth;

          return (
            <TreeItem
              node={node}
              depth={adjustedDepth}
              isLastChild={node.isLastChild}
              isLastSticky={isLastSticky}
              key={virtualItem.key}
              stickyIndex={isSticky ? virtualItem.index : undefined}
              stickyElsRef={stickyElsRef}
              style={{
                position: isSticky ? "sticky" : "absolute",
                top: isSticky
                  ? `${offsetTop + stickyAncestorHeightByIndex[virtualItem.index]}px`
                  : 0,
                transform: isSticky
                  ? "translateY(var(--sticky-push-offset, 0px))"
                  : `translateY(${virtualItem.start}px)`,
                height: `${virtualItem.size}px`,
                left: 0,
                width: "100%",
                zIndex: node.ariaHidden ? 0 : 20 - node.depth,
              }}
            >
              {node.render({
                ...node,
                depth: adjustedDepth,
              })}
            </TreeItem>
          );
        })}
      </div>
    </div>
  );
}
